import { useState, useEffect } from 'react'
import { observer } from 'mobx-react'
import { Col, Row, Typography, Divider, Spin } from 'antd'
import moment from 'moment'
import { useParams, useLocation, Link } from 'react-router-dom'
import { useChilizStore } from '../stores'
import { INFT } from '../stores/ChilizStore'
import { ExplorerLink } from '../components/ExplorerLink'
import { NFTTrait } from '../components/NFTTrait'
import { NFTCover } from '../components/NFTCover'
import { ReactComponent as BackIcon } from '../assets/icons/Back.svg'

import Page from './Page'

const { Title, Text } = Typography

interface IRouteParams {
  collectionAddress: string
  tokenId: string
}

interface IPreviousPage {
  path: string
  name: string
}

const BackLink = ({ previousPage }: {previousPage: IPreviousPage}): JSX.Element => {
  const { path, name } = previousPage

  return <>
    <Link to={path} className='header-breadcrumb-link'><BackIcon/><span>{name}</span> </Link>
    <span style={{ color: '#CC1024' }}>/</span>
  </>
}

const NftPage = observer(() => {
  const store = useChilizStore()
  const [nft, setNft] = useState<INFT | undefined>()
  const [previousPage, setPreviousPage] = useState<IPreviousPage | undefined>()
  const params = useParams() as any as IRouteParams
  const location = useLocation()

  useEffect(() => {
    const { state } = location
    if (typeof state === 'undefined' || state === null) {
      return
    }
    setPreviousPage(state.from)
  })

  useEffect(() => {
    if (!store.isConnected) {
      return
    }
    const fetchNft = async (): Promise<void> => {
      const nft = await store.getNft(params.collectionAddress, Number(params.tokenId))
      setNft(nft)
    }
    void fetchNft()
  }, [store.isConnected])

  if (!store.isConnected) {
    return <Page className={'pd-10'}><h1>Connecting... </h1><h2>Verify the network you are connnected to.</h2></Page>
  }

  if (typeof nft === 'undefined') {
    return (
    <Page className={'pd-10'}>
      <Row justify="center">
        <Col lg={3} sm={24} xs={24} md={24} key={'loader'} >
          <div className='collection-loader'>Loading... <Spin /></div>
        </Col>
      </Row>
    </Page>
    )
  }

  return (
    <Page className={'pd-top-page'}>
    <Col>
      <Row>
        <Col span={24}>
          <Title level={1} className="page-title breadcrumbs">
            {typeof previousPage !== 'undefined' && <BackLink previousPage={previousPage} />} NFT
          </Title>
          <Divider />
        </Col>
      </Row>
      <Row>
        <Col lg={11} sm={24} style={{ marginBottom: '30px' }}>
          <NFTCover cover={nft.image} style={{ width: '100%', marginBottom: '40px' }}/>
          <Title style={{ fontFamily: 'PP Neue Machina', fontWeight: 400, marginBottom: 0 }} level={2}>{nft.name}</Title>
          <Title style={{ fontFamily: 'PP Neue Machina', fontWeight: 400, color: '#B6B9BF', marginTop: 0, marginBottom: '20px' }} level={3}># {nft.tokenId}</Title>
          <Text style={{ fontWeight: '450', fontSize: '20px', lineHeight: '30px', color: '#FFFFFF' }}>{nft.description}</Text>
        </Col>
        <Col lg={1} sm={0}><div style={{ border: '1px solid #FFFFFF', height: '100%', width: 0, margin: '0 auto' }}></div></Col>
        <Col lg={11} sm={24}>
          <Title style={{ fontFamily: 'PP Neue Machina', fontWeight: 400, marginBottom: 0 }} level={2}>Details</Title>
          <Divider />
          <ul className='nft-details-list'>
            <li><Text className='list-item-title'>Contract Address:</Text> <Text className='list-item-value'><ExplorerLink hash={nft.address} type='address' customTitle={`${nft.address.substring(0, 22)}...`}/></Text></li>
            {typeof nft.mintDate !== 'undefined' && <li><Text className='list-item-title'>Mint Date:</Text> <Text className='list-item-value'>{moment.unix(nft.mintDate).format('L LT')}</Text></li>}
            {!nft.isRandom && <li><Text className='list-item-title'>Number:</Text> <Text className='list-item-value'>{nft.tokenId} of {nft.maxSupply}</Text></li>}
            <li><Text className='list-item-title'>Collection:</Text> <Text className='list-item-value'>{nft.collectionName}</Text></li>
            <li><Text className='list-item-title'>Blockchain:</Text> <Text className='list-item-value'>{nft.chain}</Text></li>
          </ul>
          {nft.properties.length > 0 && (
            <>
              <Title style={{ fontFamily: 'PP Neue Machina', fontWeight: 400, marginBottom: 0 }} level={2}>Properties</Title>
              <Divider />
              {nft.properties.map((p, i) => <NFTTrait key={i} name={p.trait_type} variant={p.value} />)}
            </>
          )}
        </Col>
      </Row>
    </Col>
    </Page>
  )
})

export default NftPage
